import React, {
    useEffect,
    useRef,
    useImperativeHandle,
    forwardRef,
    Ref,
    MutableRefObject
} from 'react';
import mapboxgl, { GeoJSONSource, LngLat, Marker } from 'mapbox-gl';
import { useStore } from '@vromo/mobx-react';
import { observer } from 'mobx-react-lite';
import { Site } from '@vromo/models';
import useStylesheets from '@vromo/hooks/useStylesheets';
import theme from '@vromo/ui/components/theme';
import circle from '@turf/circle';
import { autorun, IReactionDisposer } from 'mobx';

import MapboxStyle from 'mapbox-gl/dist/mapbox-gl.css';
import { mapboxStyles, secrets } from '@vromo/api';

type Props = {
    site: Site;
    onCenterChanged: ([lon, lat]: number[]) => void;
    width?: string;
    height: number;
};

export default observer(
    function Map({ site, onCenterChanged, width = '100%', height = 500 }: Props, ref: Ref<any>) {
        const mapRef = useRef();
        const mapContainerRef = useRef();
        const { viewStore } = useStore('rootStore');

        useStylesheets(MapboxStyle);

        useImperativeHandle(ref, () => ({
            setCenter(center) {
                // lon, lat
                if (mapRef.current) mapRef.current.setCenter(center);
            },
            setZoom(zoom) {
                if (mapRef.current) mapRef.current.setZoom(zoom);
            }
        }));

        useEffect(() => {
            if (!mapboxgl.supported()) {
                return;
            }

            mapboxgl.accessToken = secrets.mapbox;


            const map = new mapboxgl.Map({
                container: mapContainerRef.current,
                style: viewStore.darkMode
                    ? mapboxStyles.dark
                    : mapboxStyles.light,
                refreshExpiredTiles: false,
                zoom: site.coords ? 17 : 1,
                center: [site.coords?.lon || 1, site.coords?.lat || 1]
            });

            mapRef.current = map;

            const subscriptions: Array<IReactionDisposer> = [];

            map.on('load', () => {
                const radius = circle([site.coords?.lon || 1, site.coords?.lat || 1], 50, {
                    steps: 300,
                    units: 'meters'
                });

                const marker = new mapboxgl.Marker({ color: theme.primary, draggable: true })
                    .setLngLat([site.coords?.lon || 1, site.coords?.lat || 1])
                    .addTo(map);

                map.addSource('site-radius', {
                    type: 'geojson',
                    data: radius
                });

                map.addLayer({
                    id: 'circle-fill',
                    type: 'fill',
                    source: 'site-radius',
                    paint: {
                        'fill-color': theme.primary,
                        'fill-opacity': 0.5,
                        'fill-outline-color': theme.primary
                    }
                });

                map.addLayer({
                    id: 'circle-border',
                    type: 'line',
                    source: 'site-radius',
                    paint: {
                        'line-color': theme.primary,
                        'line-width': 1
                    }
                });

                map.on('click', (e) => {
                    onCenterChanged(e.lngLat.toArray());
                    marker.setLngLat(e.lngLat);
                    map.getSource('site-radius').setData(
                        circle(marker.getLngLat().toArray(), site.radius || 50, {
                            units: 'meters'
                        })
                    );
                    map.flyTo({ center: e.lngLat });
                });

                marker.on('dragend', () => {
                    onCenterChanged(marker.getLngLat().toArray());
                    map.getSource('site-radius').setData(
                        circle(marker.getLngLat().toArray(), site.radius || 50, {
                            units: 'meters'
                        })
                    );
                    map.flyTo({ center: marker.getLngLat() });
                });

                subscriptions.push(
                    autorun(() => {
                        const siteRadius = site.radius;

                        map.getSource('site-radius').setData(
                            circle(marker.getLngLat().toArray(), siteRadius || 50, {
                                units: 'meters'
                            })
                        );

                        map.flyTo({ center: marker.getLngLat(), zoom: 16 });
                    }),
                    autorun(() => {
                        const siteCoords = site.coords;

                        marker.setLngLat(new LngLat(siteCoords?.lon || 0, siteCoords?.lat || 0));
                        map.getSource('site-radius').setData(
                            circle(marker.getLngLat().toArray(), site.radius || 50, {
                                units: 'meters'
                            })
                        );

                        map.flyTo({ center: marker.getLngLat(), zoom: 16 });
                    })
                );
            });

            return () => {
                subscriptions.forEach((unsubscribe) => unsubscribe());
                mapRef.current.remove();
            };
        }, []);

        return (
            <div>
                <div ref={mapContainerRef} style={{ width, height }} />
            </div>
        );
    },
    { forwardRef: true }
);
